/**
 * t216u: one Back for every phone detail page. Inside the app (React Router's history index > 0) it goes back a step,
 * so the page underneath restores its scroll (t184u); opened straight from a link, it goes to `fallback` instead.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { useBackToOrigin } from '@/lib/origin';

export const canGoBackInApp = () => typeof window !== 'undefined' && ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;

export function useSmartBack(fallback: string) {
  const nav = useNavigate();
  return () => (canGoBackInApp() ? nav(-1) : nav(fallback, { replace: true }));
}

/**
 * t257u: where a phone chat's Back goes. A page that named itself as the origin (Home widgets, a building) wins;
 * otherwise a chat opened inside the app (Who's in, the person sheet, the rota, a feed name, a notification, or the
 * Chat tab's own list) goes back a step, so that page comes back with its building, search and scroll. A chat opened
 * straight from a link, a cold start or a push tap (`direct` navigation state) goes to the chat list.
 */
export type ChatBackTarget = 'origin' | 'history' | 'chat';
export function chatBackTarget({ hasOrigin, inApp, direct }: { hasOrigin: boolean; inApp: boolean; direct: boolean }): ChatBackTarget {
  if (hasOrigin) return 'origin';
  return inApp && !direct ? 'history' : 'chat';
}
/** Navigation state for an in-app navigation that should count as opened directly (a push tap while the app is open). */
export const DIRECT_NAV_STATE = { direct: true } as const;
export const isDirectNav = (state: unknown) => Boolean((state as { direct?: boolean } | null)?.direct);

/**
 * t260u: the same rule for any detail page's Back (an announcement on a computer or a phone): the origin the page was
 * opened from (Home's feed, with its scroll), else a step back in the app (the announcements list, a notification),
 * else (a link, a cold start, a push tap) `fallback`.
 */
export function useDetailBack(fallback: string) {
  const { origin, back } = useBackToOrigin();
  const nav = useNavigate();
  const loc = useLocation();
  const target = chatBackTarget({ hasOrigin: Boolean(origin), inApp: canGoBackInApp(), direct: isDirectNav(loc.state) });
  return {
    target, origin,
    go: () => (target === 'origin' ? back() : target === 'history' ? nav(-1) : nav(fallback, { replace: true })),
  };
}
